import React, { useCallback, useEffect, useState } from 'react';
import {
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  useExtractKalturaScript,
} from '@dcl/ui';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { ITrainingExperienceVideos, IVideoPlaylistDetail } from './types';
import fetchApi from '../../utils/fetchApi';
import {
  CREATE_USER_SESSION_API,
  GET_VIDEO_DATA_API,
  VIDEO_PLAYER,
  VIDEO_PLAY_LIST,
} from './utils/constants';
import { VideoPlayerList } from './components/VideoPlayerList/VideoPlayerList';
import { VideoPlayer } from './components/VideoPlayer/VideoPlayer';
import './TrainingExperienceVideos.css';

export const TrainingExperienceVideos: React.FC<ITrainingExperienceVideos> = (
  props
) => {
  const { partnerId, secret, privileges, entryId, title, subTitle } = props;
  const [userKeyKs, setUserKeyKs] = useState<string>('');
  const getClassName = getClassNameFactory(
    TrainingExperienceVideos.displayName
  );
  const [videoDetailData, setVideoDetailData] = useState<
    IVideoPlaylistDetail[]
  >();
  const [videoPlayerType, setVideoPlayerType] = useState<string>('');

  useExtractKalturaScript();

  const initiateUserKeyApi = useCallback(async () => {
    const response = await fetchApi({
      baseHostUrl: CREATE_USER_SESSION_API,
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: 1,
          type: 2,
          partnerId: partnerId,
          secret: secret,
          privileges: privileges,
        }),
      },
    });

    const sessionKey = await response.text().then((data) => JSON.parse(data));
    setUserKeyKs(sessionKey);
  }, [partnerId, privileges, secret]);

  useEffect(() => {
    initiateUserKeyApi();
  }, [initiateUserKeyApi]);

  const initiateVideoDataApi = useCallback(async () => {
    const videoResponse = await fetchApi({
      baseHostUrl: GET_VIDEO_DATA_API,
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: entryId,
          version: -1,
          format: 1,
          ks: userKeyKs,
        }),
      },
    });

    const videoMetaData = await videoResponse
      .text()
      .then((data) => JSON.parse(data));

    let videoPlayerData: IVideoPlaylistDetail[] = [];
    if (videoMetaData.objectType === 'KalturaPlaylist') {
      const playlistContent = videoMetaData.playlistContent;
      videoPlayerData = playlistContent.split(',').map((entry: string) => {
        return {
          entry_id: entry,
          videoFormat: 'Video',
        };
      });
      setVideoDetailData(videoPlayerData);
      setVideoPlayerType('VideoPlayList');
    } else {
      setVideoPlayerType('VideoPlayer');
    }
  }, [entryId, userKeyKs]);

  useEffect(() => {
    initiateVideoDataApi();
  }, [initiateVideoDataApi]);

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(
        TrainingExperienceVideos.displayName
      )}
      {...propsToDataAttributes(props)}
    >
      {videoPlayerType === VIDEO_PLAYER && (
        <VideoPlayer entry_ID={entryId} ks={userKeyKs} />
      )}

      {videoPlayerType === VIDEO_PLAY_LIST && (
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col
              lg={8}
              sm={12}
              md={12}
              className={getClassName({ descendantName: 'videoHeading' })}
            >
              <VideoPlayerList
                ks={userKeyKs}
                subTitle={subTitle}
                title={title}
                videoDetails={videoDetailData!}
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      )}
    </div>
  );
};

TrainingExperienceVideos.displayName = 'TrainingExperienceVideos';
